/**
 * L5 HTTP failure reporter — 4xx/5xx 响应 → console.error → error-reporter
 * → POST /__dev/log → server stdout → make test-l5 / Monitor 实时看到。
 *
 * 为什么需要:HTTP 4xx/5xx 是正常 Response,不触发 window.onerror。
 * 应用代码通常把它丢进 React Query 的 error state,不主动 console.error
 * → 浏览器 DevTools 之外没人知道 → L5 监听看不到。这个 wrapper 强制
 * console.error,让 error-reporter 的 console.error hook 拿到。
 *
 * 用法:
 *   1. supabase.ts: createClient({ global: { fetch: makeReportingFetch() } })
 *   2. client.ts(apiClient): reportHttpFailure(url, status, bodyPreview) 在 throw 前
 *
 * Gate 与 error-reporter 一致:import.meta.env.DEV || VITE_DEV_REPORTER === 'true'。
 * Prod CI 构建该常量为 false,以下函数被 dead-code 消除。
 */

const BODY_PREVIEW_MAX = 500

function reportEnabled(): boolean {
  const meta = import.meta as { env?: { DEV?: boolean; VITE_DEV_REPORTER?: string } }
  return meta.env?.DEV === true || meta.env?.VITE_DEV_REPORTER === 'true'
}

export function reportHttpFailure(url: string, status: number, bodyPreview?: string): void {
  if (!reportEnabled()) return
  const preview = bodyPreview ? bodyPreview.slice(0, BODY_PREVIEW_MAX) : ''
  // [http <status>] <url> <body preview>
  // 前缀 [http] 让后端 stdout / Monitor 容易识别筛选
  // eslint-disable-next-line no-console
  console.error(`[http ${status}] ${url}${preview ? ' — ' + preview : ''}`)
}

/**
 * makeReportingFetch 返回一个 fetch wrapper,转发到全局 fetch + 在 !ok 时 console.error。
 * gate 关时返回 undefined(调用方传 undefined 给 supabase-js,supabase 用默认 fetch)。
 *
 * 注意:wrapper 只读 response.clone().text() 取 preview,不影响原 response 流。
 */
export function makeReportingFetch(): typeof fetch | undefined {
  if (!reportEnabled()) return undefined
  return async (input, init) => {
    const res = await fetch(input, init)
    if (!res.ok) {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url
      let preview = ''
      try {
        preview = await res.clone().text()
      } catch {
        // body 已读 / 不可 clone — 忽略
      }
      reportHttpFailure(url, res.status, preview)
    }
    return res
  }
}
